import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel, Stat, SandboxNotice, StatusPill, fmtMoney, fmtDate } from "@/components/Peep";

function Growth({ series }) {
  const max = Math.max(1, ...series.map((s) => Math.max(s.gained, s.lost)));
  return (
    <div data-testid="followers-growth" className="flex h-24 items-end gap-[3px]">
      {series.map((s) => (
        <div key={s.date} title={`${s.date}: +${s.gained} / -${s.lost}`} className="flex flex-1 flex-col justify-end gap-[2px]">
          <div className="rounded-sm bg-[#a03cff]" style={{ height: `${(s.gained / max) * 70}px` }} />
          <div className="rounded-sm bg-red-400/60" style={{ height: `${(s.lost / max) * 20}px` }} />
          <div className="h-px bg-white/10" />
        </div>
      ))}
    </div>
  );
}

export function Followers({ f }) {
  return (
    <Panel title="Followers (free) vs paying subscribers">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Followers" value={f.total} testid="dash-followers" />
        <Stat label="New followers (7d)" value={f.gained_7d} testid="dash-followers-7d" />
        <Stat label="Unfollows (30d)" value={f.lost_30d} testid="dash-unfollows" />
        <Stat label="Followers who also pay" value={f.paying_followers} testid="dash-paying-followers" />
      </div>
      <p className="mt-4 text-xs text-[#a7a0af]">Follower growth · last 30 days (purple = new, red = unfollows)</p>
      <Growth series={f.series} />
      {f.followers.length === 0 ? <p data-testid="followers-empty" className="mt-3 text-sm text-[#a7a0af]">No followers yet.</p> : (
        <ul data-testid="followers-list" className="mt-3 flex max-h-64 flex-col gap-2 overflow-y-auto">
          {f.followers.map((x, i) => (
            <li key={i} className="flex items-center justify-between text-sm">
              <span className={x.private ? "text-[#a7a0af]" : ""}>{x.private ? "Private follower" : x.name}</span>
              <span className="flex items-center gap-2 text-xs text-[#a7a0af]">{fmtDate(x.since)} {x.is_member ? <StatusPill status="active" /> : <span className="text-[0.65rem]">Free follower</span>}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  useEffect(() => {
    api.get("/creator/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/creator/followers").then((r) => setF(r.data)).catch(() => {});
  }, []);
  if (!d) return <p className="text-sm text-[#a7a0af]">Loading…</p>;
  const a = d.analytics;
  return (
    <div data-testid="creator-dashboard" className="flex flex-col gap-6">
      <div>
        <h1 data-testid="creator-dashboard-name" className="text-3xl font-extrabold">{d.creator.display_name}</h1>
        <p className="mt-1 text-sm text-[#a7a0af]">@{d.creator.handle} · your private studio</p>
      </div>
      <SandboxNotice />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Followers" value={a.followers} testid="dash-followers-top" />
        <Stat label="Direct members" value={a.active_direct} testid="dash-direct" />
        <Stat label="Via All Access" value={a.active_all_access} testid="dash-aa" />
        <Stat label="New (30d)" value={a.new_30d} />
        <Stat label="Cancellations (30d)" value={a.canceled_30d} />
        <Stat label="Published" value={a.published_items} />
        <Stat label="Drafts" value={a.draft_items} />
        <Stat label="Gross revenue" value={fmtMoney(d.revenue.gross)} testid="dash-gross" />
        <Stat label="Your earnings" value={fmtMoney(d.revenue.earnings)} testid="dash-earnings" />
      </div>
      {f && <Followers f={f} />}
      <Panel title="Recent members">
        {d.recent_subscribers.length === 0 ? (
          <p className="text-sm text-[#a7a0af]">No direct members yet.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {d.recent_subscribers.map((s, i) => (
              <li key={i} className="flex items-center justify-between text-sm">
                <span>{s.name}</span>
                <span className="flex items-center gap-2 text-xs text-[#a7a0af]">{fmtDate(s.when)} <StatusPill status={s.status} /></span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
